/**
 * Store Page Component
 *
 * Displays store pages (legal pages, about, contact, and custom pages).
 * Fetches content from database if available, otherwise shows default content.
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection, type LanguageCode } from '../../i18n/languages';
import StoreFooter from '../../components/storefront/StoreFooter';
import CookieConsent from '../../components/storefront/CookieConsent';
import ReactMarkdown from 'react-markdown';

interface Store {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  contact_email?: string;
  theme: StoreTheme;
  locale: LanguageCode;
  social_links?: Record<string, string>;
}

interface StoreTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily?: string;
}

interface PageContent {
  id: string;
  page_type: string;
  slug: string | null;
  title: string;
  content: string;
  meta_description: string | null;
  meta_keywords: string | null;
  updated_at: string;
}

const defaultTheme: StoreTheme = {
  primaryColor: '#0d9488',
  secondaryColor: '#6366f1',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
};

type PageType = 'privacy' | 'terms' | 'cookies' | 'refund' | 'about' | 'contact' | 'custom';

export default function LegalPage() {
  const { storeSlug, pageSlug } = useParams<{ storeSlug: string; pageSlug?: string }>();
  const location = useLocation();
  const { t, i18n } = useTranslation('store');

  // Determine page type from URL
  const pathParts = location.pathname.split('/');
  const isCustomPage = pathParts.includes('page');
  const pageType: PageType = isCustomPage ? 'custom' : (pathParts.pop() as PageType);
  const effectiveSlug = isCustomPage ? pageSlug : pageType;

  const [store, setStore] = useState<Store | null>(null);
  const [pageContent, setPageContent] = useState<PageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        // First, fetch store data
        const storeResponse = await fetch(`/api/public/stores/${storeSlug}`);
        if (!storeResponse.ok) {
          throw new Error('Store not found');
        }
        const storeData = await storeResponse.json() as { store: Store };
        setStore(storeData.store);

        // Set language if store has a locale
        if (storeData.store.locale && storeData.store.locale !== i18n.language) {
          i18n.changeLanguage(storeData.store.locale);
        }

        // Then, try to fetch page content from database
        try {
          const pageResponse = await fetch(`/api/public/stores/${storeSlug}/pages/${effectiveSlug}`);
          if (pageResponse.ok) {
            const pageData = await pageResponse.json() as { page: PageContent };
            setPageContent(pageData.page);
          }
          // If page doesn't exist in DB, we'll show default content
        } catch {
          // Page not found in DB, will use default content
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load page');
      } finally {
        setLoading(false);
      }
    }

    if (storeSlug) {
      fetchData();
    }
  }, [storeSlug, effectiveSlug, i18n]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('storefront.storeNotFound', 'Store Not Found')}
          </h1>
          <p className="text-gray-600 mb-4">
            {t('storefront.storeNotFoundDescription', "The store you're looking for doesn't exist.")}
          </p>
          <Link to="/" className="text-teal-600 hover:underline">
            {t('storefront.goToHomepage', 'Go to Homepage')}
          </Link>
        </div>
      </div>
    );
  }

  // If custom page and no content found
  if (isCustomPage && !pageContent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('storefront.pageNotFound', 'Page Not Found')}
          </h1>
          <p className="text-gray-600 mb-4">
            {t('storefront.pageNotFoundDescription', "The page you're looking for doesn't exist.")}
          </p>
          <Link to={`/s/${storeSlug}`} className="text-teal-600 hover:underline">
            {t('product.backToStore', 'Back to Store')}
          </Link>
        </div>
      </div>
    );
  }

  const theme = store.theme || defaultTheme;
  const direction = getLanguageDirection(store.locale || 'en');

  // Get page title
  const getPageTitle = (): string => {
    if (pageContent?.title) return pageContent.title;

    const defaultTitles: Record<string, string> = {
      privacy: t('footer.privacyPolicy', 'Privacy Policy'),
      terms: t('footer.termsOfService', 'Terms of Service'),
      cookies: t('footer.cookiePolicy', 'Cookie Policy'),
      refund: t('footer.refundPolicy', 'Refund Policy'),
      about: t('footer.aboutUs', 'About Us'),
      contact: t('footer.contactUs', 'Contact Us'),
    };
    return defaultTitles[pageType] || 'Page';
  };

  // Get last updated date
  const getLastUpdated = (): string => {
    if (pageContent?.updated_at) {
      return new Date(pageContent.updated_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Render content - from DB if available, otherwise default
  const renderContent = () => {
    if (pageContent?.content) {
      return (
        <div className="prose prose-gray max-w-none">
          <ReactMarkdown>{pageContent.content}</ReactMarkdown>
        </div>
      );
    }

    // Fall back to default content for system pages
    switch (pageType) {
      case 'privacy':
        return <PrivacyPolicyContent store={store} t={t} />;
      case 'terms':
        return <TermsOfServiceContent store={store} t={t} />;
      case 'cookies':
        return <CookiePolicyContent store={store} t={t} />;
      case 'refund':
        return <RefundPolicyContent store={store} t={t} />;
      case 'about':
        return <AboutUsContent store={store} t={t} />;
      case 'contact':
        return <ContactUsContent store={store} t={t} />;
      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: theme.backgroundColor,
        color: theme.textColor,
        fontFamily: theme.fontFamily,
      }}
      dir={direction}
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={`/s/${store.slug}`} className="flex items-center gap-3">
              {store.logo_url ? (
                <img src={store.logo_url} alt={store.name} className="h-8 w-auto" />
              ) : (
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  {store.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="font-semibold text-gray-900">{store.name}</span>
            </Link>
            <Link
              to={`/s/${store.slug}`}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              {t('product.backToStore', 'Back to Store')}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{getPageTitle()}</h1>
          <p className="text-sm text-gray-500 mb-8">
            {t('legal.lastUpdated', 'Last updated')}: {getLastUpdated()}
          </p>

          <div className="prose prose-gray max-w-none">
            {renderContent()}
          </div>
        </div>
      </main>

      {/* Footer */}
      <StoreFooter store={store} direction={direction} />

      {/* Cookie Consent */}
      <CookieConsent
        storeId={store.id}
        storeSlug={store.slug}
        primaryColor={theme.primaryColor}
      />
    </div>
  );
}

// Default content components for system pages

function PrivacyPolicyContent({ store, t }: { store: Store; t: ReturnType<typeof useTranslation<'store'>>['t'] }) {
  return (
    <>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.privacy.intro', 'Introduction')}
        </h2>
        <p className="text-gray-600 mb-4">
          {store.name} ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and make purchases from our store.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.privacy.dataCollection', 'Information We Collect')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.privacy.dataCollectionText', 'We collect information that you provide directly to us, including:')}
        </p>
        <ul className="list-disc pl-6 text-gray-600 space-y-2">
          <li>{t('legal.privacy.dataItem1', 'Name and contact information (email address, phone number, shipping address)')}</li>
          <li>{t('legal.privacy.dataItem2', 'Payment information (processed securely through our payment providers)')}</li>
          <li>{t('legal.privacy.dataItem3', 'Order history and purchase preferences')}</li>
          <li>{t('legal.privacy.dataItem4', 'Communications you send to us')}</li>
          <li>{t('legal.privacy.dataItem5', 'Device and browser information collected automatically')}</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.privacy.dataUse', 'How We Use Your Information')}
        </h2>
        <ul className="list-disc pl-6 text-gray-600 space-y-2">
          <li>{t('legal.privacy.useItem1', 'Process and fulfill your orders')}</li>
          <li>{t('legal.privacy.useItem2', 'Communicate with you about your orders and our products')}</li>
          <li>{t('legal.privacy.useItem3', 'Improve our website and services')}</li>
          <li>{t('legal.privacy.useItem4', 'Send you marketing communications (with your consent)')}</li>
          <li>{t('legal.privacy.useItem5', 'Comply with legal obligations')}</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.privacy.gdprRights', 'Your Rights (GDPR)')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.privacy.gdprRightsText', 'If you are located in the European Economic Area, you have the right to:')}
        </p>
        <ul className="list-disc pl-6 text-gray-600 space-y-2">
          <li>{t('legal.privacy.gdprItem1', 'Access your personal data')}</li>
          <li>{t('legal.privacy.gdprItem2', 'Correct inaccurate data')}</li>
          <li>{t('legal.privacy.gdprItem3', 'Request deletion of your data')}</li>
          <li>{t('legal.privacy.gdprItem4', 'Object to or restrict processing')}</li>
          <li>{t('legal.privacy.gdprItem5', 'Data portability')}</li>
          <li>{t('legal.privacy.gdprItem6', 'Withdraw consent at any time')}</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.privacy.contact', 'Contact Us')}
        </h2>
        <p className="text-gray-600">
          {t('legal.privacy.contactText', 'If you have questions about this Privacy Policy, please contact us at:')}
          {store.contact_email && (
            <> <a href={`mailto:${store.contact_email}`} className="text-teal-600 hover:underline">{store.contact_email}</a></>
          )}
        </p>
      </section>
    </>
  );
}

function TermsOfServiceContent({ store, t }: { store: Store; t: ReturnType<typeof useTranslation<'store'>>['t'] }) {
  return (
    <>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.terms.acceptance', 'Acceptance of Terms')}
        </h2>
        <p className="text-gray-600 mb-4">
          By accessing and using {store.name}'s website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.terms.products', 'Products and Services')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.terms.productsText', 'We make every effort to display our products accurately. However, we cannot guarantee that your device display will accurately reflect the actual colors or details of our products. We reserve the right to limit quantities, refuse orders, or discontinue products at any time.')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.terms.pricing', 'Pricing and Payment')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.terms.pricingText', 'All prices are displayed in the currency indicated on our website and are subject to change without notice. Payment must be made at the time of purchase through our available payment methods.')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.terms.shipping', 'Shipping and Delivery')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.terms.shippingText', 'Delivery times are estimates and not guaranteed. We are not responsible for delays caused by customs, weather, or carrier issues.')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.terms.liability', 'Limitation of Liability')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.terms.liabilityText', 'To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services or products.')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.terms.contact', 'Contact Information')}
        </h2>
        <p className="text-gray-600">
          {t('legal.terms.contactText', 'For questions about these Terms of Service, contact us at:')}
          {store.contact_email && (
            <> <a href={`mailto:${store.contact_email}`} className="text-teal-600 hover:underline">{store.contact_email}</a></>
          )}
        </p>
      </section>
    </>
  );
}

function CookiePolicyContent({ store, t }: { store: Store; t: ReturnType<typeof useTranslation<'store'>>['t'] }) {
  return (
    <>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.cookies.whatAre', 'What Are Cookies')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.cookies.whatAreText', 'Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and enabling certain features.')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.cookies.typesUsed', 'Types of Cookies We Use')}
        </h2>
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">{t('cookie.necessary', 'Necessary Cookies')}</h3>
            <p className="text-gray-600 text-sm">
              {t('legal.cookies.necessaryText', 'Essential for the website to function. They enable core functionality such as security, shopping cart, and checkout processes.')}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">{t('cookie.analytics', 'Analytics Cookies')}</h3>
            <p className="text-gray-600 text-sm">
              {t('legal.cookies.analyticsText', 'Help us understand how visitors interact with our website by collecting and reporting information anonymously.')}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">{t('cookie.marketing', 'Marketing Cookies')}</h3>
            <p className="text-gray-600 text-sm">
              {t('legal.cookies.marketingText', 'Used to track visitors across websites to display relevant advertisements based on their interests.')}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">{t('cookie.preferences', 'Preference Cookies')}</h3>
            <p className="text-gray-600 text-sm">
              {t('legal.cookies.preferencesText', 'Allow the website to remember choices you make and provide enhanced, personalized features.')}
            </p>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.cookies.manage', 'Managing Cookies')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.cookies.manageText', 'You can manage your cookie preferences through our cookie consent banner. Most web browsers also allow you to control cookies through their settings.')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.cookies.contact', 'Contact Us')}
        </h2>
        <p className="text-gray-600">
          {t('legal.cookies.contactText', 'If you have questions about our use of cookies, please contact us at:')}
          {store.contact_email && (
            <> <a href={`mailto:${store.contact_email}`} className="text-teal-600 hover:underline">{store.contact_email}</a></>
          )}
        </p>
      </section>
    </>
  );
}

function RefundPolicyContent({ store, t }: { store: Store; t: ReturnType<typeof useTranslation<'store'>>['t'] }) {
  return (
    <>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.refund.overview', 'Refund Policy Overview')}
        </h2>
        <p className="text-gray-600 mb-4">
          At {store.name}, we want you to be completely satisfied with your purchase. If you are not satisfied, we offer refunds and exchanges under the conditions outlined below.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.refund.eligibility', 'Refund Eligibility')}
        </h2>
        <ul className="list-disc pl-6 text-gray-600 space-y-2">
          <li>{t('legal.refund.eligItem1', 'Items must be returned within 30 days of delivery')}</li>
          <li>{t('legal.refund.eligItem2', 'Items must be unused and in original condition')}</li>
          <li>{t('legal.refund.eligItem3', 'Items must be in original packaging with all tags attached')}</li>
          <li>{t('legal.refund.eligItem4', 'Proof of purchase is required')}</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.refund.nonRefundable', 'Non-Refundable Items')}
        </h2>
        <ul className="list-disc pl-6 text-gray-600 space-y-2">
          <li>{t('legal.refund.nonItem1', 'Digital products and downloads')}</li>
          <li>{t('legal.refund.nonItem2', 'Gift cards')}</li>
          <li>{t('legal.refund.nonItem3', 'Personalized or custom-made items')}</li>
          <li>{t('legal.refund.nonItem4', 'Sale items (unless defective)')}</li>
          <li>{t('legal.refund.nonItem5', 'Items marked as final sale')}</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.refund.process', 'Refund Process')}
        </h2>
        <ol className="list-decimal pl-6 text-gray-600 space-y-2">
          <li>{t('legal.refund.step1', 'Contact our customer service with your order number')}</li>
          <li>{t('legal.refund.step2', 'Receive return authorization and shipping instructions')}</li>
          <li>{t('legal.refund.step3', 'Ship the item back (customer pays return shipping unless item is defective)')}</li>
          <li>{t('legal.refund.step4', 'Refund will be processed within 5-10 business days after we receive the item')}</li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.refund.contact', 'Contact Us')}
        </h2>
        <p className="text-gray-600">
          {t('legal.refund.contactText', 'For refund requests or questions, please contact us at:')}
          {store.contact_email && (
            <> <a href={`mailto:${store.contact_email}`} className="text-teal-600 hover:underline">{store.contact_email}</a></>
          )}
        </p>
      </section>
    </>
  );
}

function AboutUsContent({ store, t }: { store: Store; t: ReturnType<typeof useTranslation<'store'>>['t'] }) {
  return (
    <>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.about.welcome', 'Welcome')}
        </h2>
        <p className="text-gray-600 mb-4">
          Welcome to {store.name}! We're glad you're here.
        </p>
        {store.description && (
          <p className="text-gray-600 mb-4">{store.description}</p>
        )}
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.about.mission', 'Our Mission')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.about.missionText', 'We are committed to providing quality products and excellent customer service to our valued customers.')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.about.values', 'Our Values')}
        </h2>
        <ul className="list-disc pl-6 text-gray-600 space-y-2">
          <li>{t('legal.about.value1', 'Quality products at fair prices')}</li>
          <li>{t('legal.about.value2', 'Exceptional customer service')}</li>
          <li>{t('legal.about.value3', 'Fast and reliable shipping')}</li>
          <li>{t('legal.about.value4', 'Sustainable and responsible practices')}</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.about.contact', 'Get in Touch')}
        </h2>
        <p className="text-gray-600">
          {t('legal.about.contactText', "We'd love to hear from you! Contact us at:")}
          {store.contact_email && (
            <> <a href={`mailto:${store.contact_email}`} className="text-teal-600 hover:underline">{store.contact_email}</a></>
          )}
        </p>
      </section>
    </>
  );
}

function ContactUsContent({ store, t }: { store: Store; t: ReturnType<typeof useTranslation<'store'>>['t'] }) {
  return (
    <>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.contact.getInTouch', 'Get in Touch')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('legal.contact.intro', "We're here to help! Reach out to us with any questions, concerns, or feedback.")}
        </p>
      </section>

      {store.contact_email && (
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t('legal.contact.email', 'Email')}
          </h2>
          <p className="text-gray-600">
            <a href={`mailto:${store.contact_email}`} className="text-teal-600 hover:underline">
              {store.contact_email}
            </a>
          </p>
        </section>
      )}

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.contact.hours', 'Business Hours')}
        </h2>
        <p className="text-gray-600">
          {t('legal.contact.hoursText', 'Monday - Friday: 9:00 AM - 5:00 PM')}
        </p>
        <p className="text-gray-600">
          {t('legal.contact.hoursWeekend', 'Saturday - Sunday: Closed')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('legal.contact.response', 'Response Time')}
        </h2>
        <p className="text-gray-600">
          {t('legal.contact.responseText', 'We aim to respond to all inquiries within 24-48 business hours.')}
        </p>
      </section>

      {store.social_links && Object.keys(store.social_links).length > 0 && (
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t('legal.contact.social', 'Follow Us')}
          </h2>
          <div className="flex gap-4">
            {Object.entries(store.social_links).map(([platform, url]) => (
              url && (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:underline capitalize"
                >
                  {platform}
                </a>
              )
            ))}
          </div>
        </section>
      )}
    </>
  );
}
